import { useEffect, useState } from 'react';
import { X, Loader2, Mail, Ticket, CalendarClock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getStudentOnboardingInfo, resendStudentPassword } from '@/api/stnApi';
import { cn } from '@/lib/utils';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

// Per-intern resend of the onboarding email. When the intern holds an onboarding
// coupon, the dialog shows the code + expiry and lets the founder push the deadline
// forward before the email goes out (so the resent mail carries the new date).
export default function ResendOnboardingModal({ batchId, studentId, studentName, onClose, onDone }) {
  const [info, setInfo]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [extend, setExtend]   = useState(false);
  const [newExpiry, setNewExpiry] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    getStudentOnboardingInfo(batchId, studentId)
      .then(setInfo)
      .catch((e) => toast.error(e?.message || 'Could not load coupon info'))
      .finally(() => setLoading(false));
  }, [batchId, studentId]);

  const coupon  = info?.coupon;
  const student = info?.student;
  const expired = !!coupon?.expired;

  const toggleExtend = (v) => { setExtend(v); if (v && !newExpiry) setNewExpiry(inDays(14)); };

  const send = async () => {
    if (extend && coupon && !newExpiry) { toast.error('Pick a new expiry date'); return; }
    setSending(true);
    try {
      const r = await resendStudentPassword(batchId, studentId, extend && coupon ? { new_expiry: newExpiry } : {});
      toast.success(r?.message || 'Onboarding email sent');
      onDone?.();
      onClose();
    } catch (e) { toast.error(e?.message || 'Could not send onboarding email'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0"><Mail className="w-4.5 h-4.5" /></div>
            <div className="min-w-0">
              <h3 className="font-bold text-foreground truncate">Resend onboarding</h3>
              <p className="text-xs text-muted-foreground truncate">{student?.name || studentName || 'Intern'}{student?.email ? ` · ${student.email}` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-14 justify-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-xs text-muted-foreground">
              {student?.activated
                ? 'This intern already set a password — they’ll receive a password-reset link.'
                : 'A fresh set-password (onboarding) link will be emailed.'}
              {student?.reshareCount ? ` Resent ${student.reshareCount}× · last ${fmtDate(student.lastReshared)}.` : ''}
            </p>

            {coupon ? (
              <div className={cn('rounded-xl border p-3', expired ? 'border-rose-500/30 bg-rose-500/5' : 'border-emerald-500/30 bg-emerald-500/5')}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Ticket className="w-3 h-3" /> Onboarding coupon</p>
                  {coupon.used
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Used</span>
                    : expired
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/25 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Expired</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/25">Active</span>}
                </div>
                <div className="mt-1.5 flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="font-mono font-bold text-foreground">{coupon.code}</span>
                  <span className="text-xs text-muted-foreground">{coupon.discountText}</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" /> {coupon.expiresAt ? <>Expires <b className={expired ? 'text-rose-500' : 'text-foreground'}>{fmtDate(coupon.expiresAt)}</b></> : 'No expiry'}
                </p>

                {!coupon.used && (
                  <div className="mt-3 pt-3 border-t border-border/70">
                    <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer">
                      <input type="checkbox" checked={extend} onChange={e => toggleExtend(e.target.checked)} className="accent-emerald-600" />
                      Extend the coupon deadline before sending
                    </label>
                    {extend && (
                      <div className="mt-2.5 space-y-2">
                        <input type="date" value={newExpiry} min={inDays(0)} onChange={e => setNewExpiry(e.target.value)}
                          className="h-9 px-2.5 rounded-lg border border-border bg-background text-sm w-full" />
                        <div className="flex items-center gap-1.5">
                          {[7, 14, 30].map(n => (
                            <button key={n} type="button" onClick={() => setNewExpiry(inDays(n))}
                              className={cn('text-[11px] px-2.5 py-1 rounded-full border', newExpiry === inDays(n) ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-background text-muted-foreground border-border hover:border-emerald-500/40')}>
                              +{n}d
                            </button>
                          ))}
                          <span className="text-[11px] text-muted-foreground ml-auto">New expiry: <b className="text-foreground">{fmtDate(newExpiry)}</b></span>
                        </div>
                      </div>
                    )}
                    {expired && !extend && <p className="text-[11px] text-rose-500 mt-2">Coupon is expired — extend it so the resent email carries a usable code.</p>}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs text-muted-foreground flex items-center gap-2">
                <Ticket className="w-3.5 h-3.5" /> This intern was onboarded without a coupon.
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button onClick={onClose} disabled={sending} className="h-9 px-4 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">Cancel</button>
              <button onClick={send} disabled={sending}
                className="h-9 px-4 rounded-lg bg-emerald-500 text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-emerald-600 disabled:opacity-50">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                {extend && coupon ? 'Extend & resend' : 'Resend email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
